/**
 * The urgent inspection — the movement area walked after bad weather, before
 * operations are confirmed safe.
 *
 * Heavy rain, a thunderstorm, a sandstorm, snow or ice, hail or a strong wind:
 * after any of them the aerodrome operator inspects the runways, the taxiways
 * and the aprons before it tells air traffic services the movement area is fit
 * to use. ICAO Annex 14 Vol I (§2.9, condition of the movement area) requires
 * the condition to be assessed and reported; PANS-Aerodromes (Doc 9981) sets
 * the Global Reporting Format that the runway part of this follows. The
 * national authority makes the inspection mandatory; the aerodrome's own
 * manual says who does it and how fast. This module is the arithmetic and the
 * rules; nothing in it replaces the aerodrome's procedures, and the screen
 * says so.
 *
 * THE RUNWAY IS ASSESSED IN THIRDS. Each third is given the contaminant on it,
 * its depth where depth matters, and the share of the third it covers; from
 * those the runway condition code (RWYCC, 0–6) follows the runway condition
 * assessment matrix (RCAM) in `rwyccFor`. Thirds are numbered from the
 * threshold with the LOWER designator, as the runway condition report (RCR)
 * is. Trained personnel may DOWNGRADE a code on what they observe; this module
 * never lets one be raised above the matrix.
 *
 * TAXIWAYS AND APRONS ARE A CHECKLIST, each item ok, found (the hazard is there
 * now) or cleared (it was there and has been dealt with — the debris picked
 * up, the dolly re-secured). A cleared item is not a hazard, but it stays on
 * the record: an apron that shed three containers in a storm is information
 * for the next storm.
 *
 * NOTHING IS OVERWRITTEN. A re-inspection of an area is a NEW check appended
 * to the inspection; the current state of an area is its latest check, and
 * every earlier one stays. Every action is also written to the inspection's
 * log with who and when. Operations can be confirmed safe only when every area
 * has been inspected and no hazard is open; otherwise the inspection can be
 * closed only WITH RESTRICTIONS, naming the areas that stay out of use.
 */
import type { SourceRef } from "./decision";
import type { Submission } from "./urgentForms";
import { t, tn } from "../i18n";

/* ── the movement area ────────────────────────────────────────────────── */

export type AreaKind = "runway" | "taxiway" | "apron";

export interface RunwayDef {
  /** the zone code, e.g. RWY-17-35 */
  zone: string;
  /** e.g. "17/35" */
  designator: string;
  /** the lower designator the thirds are counted from, e.g. "17" */
  from: string;
  lengthM?: number;
}

export interface SurfaceDef {
  zone: string;
  name: string;
}

export interface MovementArea {
  runways: RunwayDef[];
  taxiways: SurfaceDef[];
  aprons: SurfaceDef[];
}

export interface Area {
  /** zone code, or zone:third for a runway third */
  id: string;
  kind: AreaKind;
  zone: string;
  /** English, with the designator substituted by the caller */
  name: string;
  /** 1, 2 or 3 for a runway third, from the lower-designator threshold */
  third?: 1 | 2 | 3;
  runway?: RunwayDef;
}

export const THIRD_LABEL: Record<1 | 2 | 3, string> = {
  1: "First third",
  2: "Second third",
  3: "Last third",
};

/** Every area an urgent inspection covers: each runway third, each taxiway, each apron. */
export function areasOf(m: MovementArea): Area[] {
  const out: Area[] = [];
  for (const r of m.runways)
    for (const third of [1, 2, 3] as const)
      out.push({
        id: `${r.zone}:${third}`,
        kind: "runway",
        zone: r.zone,
        name: `RWY ${r.designator}`,
        third,
        runway: r,
      });
  for (const tw of m.taxiways) out.push({ id: tw.zone, kind: "taxiway", zone: tw.zone, name: tw.name });
  for (const ap of m.aprons) out.push({ id: ap.zone, kind: "apron", zone: ap.zone, name: ap.name });
  return out;
}

/* ── what brought the inspection on ───────────────────────────────────── */

export type TriggerKey = "rain" | "sandstorm" | "snow" | "wind" | "hail" | "other";

export const TRIGGERS: { key: TriggerKey; label: string; watch: string }[] = [
  {
    key: "rain",
    label: "Heavy rain or thunderstorm",
    watch: "standing water and ponding, blocked drains, washed-in debris, lightning damage to lights",
  },
  {
    key: "sandstorm",
    label: "Sandstorm or dust storm",
    watch: "loose sand and drifts, markings and lights covered, blown debris, equipment moved on the aprons",
  },
  {
    key: "snow",
    label: "Snow, ice or freezing precipitation",
    watch: "snow, slush, ice and frost by depth and coverage, snowbanks, lights and signs covered",
  },
  {
    key: "wind",
    label: "Strong wind or gusts",
    watch:
      "flying debris, equipment, containers and barriers moved from where they were secured, fence and screen damage",
  },
  {
    key: "hail",
    label: "Hail",
    watch: "surface and light damage, ice pellets, debris",
  },
  { key: "other", label: "Other weather event", watch: "anything that may have changed the movement area" },
];

/* ── the checks ───────────────────────────────────────────────────────── */

export type CheckKey =
  | "water"
  | "snowIce"
  | "sand"
  | "fod"
  | "damage"
  | "lights"
  | "markings"
  | "drainage"
  | "wildlife"
  | "equipment"
  | "stands"
  | "fences";

export const CHECKS: { key: CheckKey; label: string; what: string; on: AreaKind[] }[] = [
  {
    key: "water",
    label: "Standing water or flooding",
    what: "ponding, water over the surface, flooded shoulders or strips",
    on: ["taxiway", "apron"],
  },
  {
    key: "snowIce",
    label: "Snow, slush or ice",
    what: "any frozen contaminant on the surface",
    on: ["taxiway"],
  },
  {
    key: "sand",
    label: "Loose sand or drifts",
    what: "sand on the surface or drifted against lights and signs",
    on: ["runway", "taxiway"],
  },
  {
    key: "fod",
    label: "FOD or flying debris",
    what: "any foreign object on the surface or blown onto it",
    on: ["runway", "taxiway", "apron"],
  },
  {
    key: "damage",
    label: "Surface damage",
    what: "potholes, cracking, spalling, lifted joints or edges, washout",
    on: ["runway", "taxiway", "apron"],
  },
  {
    key: "lights",
    label: "Lights and signs damaged or covered",
    what: "ground lights, signs and floodlights broken, out, or covered",
    on: ["runway", "taxiway", "apron"],
  },
  {
    key: "markings",
    label: "Markings obscured",
    what: "center line, edge, holding position or stand markings not visible",
    on: ["runway", "taxiway"],
  },
  {
    key: "drainage",
    label: "Drainage blocked",
    what: "drains, channels or culverts blocked or overflowing",
    on: ["runway", "taxiway", "apron"],
  },
  {
    key: "wildlife",
    label: "Wildlife on or near the runway",
    what: "birds or animals drawn to water or insects after the weather",
    on: ["runway"],
  },
  {
    key: "equipment",
    label: "Equipment moved from its secured place",
    what: "ground equipment, containers, dollies, steps, cones or barriers shifted, loose or blown over",
    on: ["apron"],
  },
  {
    key: "stands",
    label: "Stands obstructed",
    what: "a stand, its clearance lines or the service road blocked",
    on: ["apron"],
  },
  {
    key: "fences",
    label: "Fences and screens damaged",
    what: "blast screens, fences, gates or barriers damaged",
    on: ["apron"],
  },
];

export const CHECKS_FOR: Record<AreaKind, CheckKey[]> = {
  runway: CHECKS.filter((c) => c.on.includes("runway")).map((c) => c.key),
  taxiway: CHECKS.filter((c) => c.on.includes("taxiway")).map((c) => c.key),
  apron: CHECKS.filter((c) => c.on.includes("apron")).map((c) => c.key),
};

export const CHECK_LABEL = Object.fromEntries(CHECKS.map((c) => [c.key, c.label])) as Record<CheckKey, string>;

/** ok · found (the hazard is there now) · cleared (it was, and has been dealt with) */
export type CheckState = "ok" | "found" | "cleared";

export const STATE_LABEL: Record<CheckState, string> = { ok: "No hazard", found: "Hazard", cleared: "Cleared" };

/* ── the runway: contaminant, depth, coverage, RWYCC ──────────────────── */

export type Contaminant =
  | "dry"
  | "wet"
  | "standingWater"
  | "slush"
  | "drySnow"
  | "wetSnow"
  | "compactedSnow"
  | "frost"
  | "ice"
  | "wetIce"
  | "waterOnCompactedSnow"
  | "snowOnCompactedSnow"
  | "snowOnIce";

export const CONTAMINANTS: { key: Contaminant; label: string; rcr: string; depth: boolean }[] = [
  { key: "dry", label: "Dry", rcr: "DRY", depth: false },
  { key: "wet", label: "Wet (3 mm or less)", rcr: "WET", depth: false },
  { key: "standingWater", label: "Standing water", rcr: "STANDING WATER", depth: true },
  { key: "slush", label: "Slush", rcr: "SLUSH", depth: true },
  { key: "drySnow", label: "Dry snow", rcr: "DRY SNOW", depth: true },
  { key: "wetSnow", label: "Wet snow", rcr: "WET SNOW", depth: true },
  { key: "compactedSnow", label: "Compacted snow", rcr: "COMPACTED SNOW", depth: false },
  { key: "frost", label: "Frost", rcr: "FROST", depth: false },
  { key: "ice", label: "Ice", rcr: "ICE", depth: false },
  { key: "wetIce", label: "Wet ice", rcr: "WET ICE", depth: false },
  {
    key: "waterOnCompactedSnow",
    label: "Water on top of compacted snow",
    rcr: "WATER ON TOP OF COMPACTED SNOW",
    depth: false,
  },
  {
    key: "snowOnCompactedSnow",
    label: "Dry or wet snow on top of compacted snow",
    rcr: "DRY SNOW ON TOP OF COMPACTED SNOW",
    depth: false,
  },
  { key: "snowOnIce", label: "Dry or wet snow on top of ice", rcr: "DRY SNOW ON TOP OF ICE", depth: false },
];

export const CONTAMINANT = Object.fromEntries(CONTAMINANTS.map((c) => [c.key, c])) as Record<
  Contaminant,
  (typeof CONTAMINANTS)[number]
>;

export const RWYCC_LABEL: Record<number, string> = {
  6: "6 — dry",
  5: "5 — good",
  4: "4 — good to medium",
  3: "3 — medium",
  2: "2 — medium to poor",
  1: "1 — poor",
  0: "0 — nil",
};

/** Coverage as the RCR reports it: 25, 50, 75 or 100; null below 10%, where none is reported. */
export function coverageBand(pct: number): 25 | 50 | 75 | 100 | null {
  if (pct < 10) return null;
  if (pct <= 25) return 25;
  if (pct <= 50) return 50;
  if (pct <= 75) return 75;
  return 100;
}

/**
 * The RWYCC for one third, from the RCAM.
 *
 * Below 10% coverage nothing is reported and the third is 6; from 10 to 25%
 * the coverage is reported and the third is still 6. From 25% the matrix
 * applies. The 3 mm line separates "wet" from standing water and a thin layer
 * of snow or slush from a thick one. Compacted snow is 4 only at an outside
 * air temperature of −15 °C or below.
 */
export function rwyccFor(
  c: Contaminant,
  depthMm: number | undefined,
  coveragePct: number,
  oatAtOrBelowMinus15 = false,
): number {
  if (c === "dry") return 6;
  if (coveragePct <= 25) return 6;
  const deep = (depthMm ?? 0) > 3;
  switch (c) {
    case "wet":
    case "frost":
      return 5;
    case "standingWater":
    case "slush":
      return deep ? 2 : 5;
    case "drySnow":
    case "wetSnow":
      return deep ? 3 : 5;
    case "compactedSnow":
      return oatAtOrBelowMinus15 ? 4 : 3;
    case "snowOnCompactedSnow":
      return 3;
    case "ice":
      return 1;
    case "wetIce":
    case "waterOnCompactedSnow":
    case "snowOnIce":
      return 0;
  }
}

/* ── one check of one area ────────────────────────────────────────────── */

export interface AreaCheck {
  id: string;
  areaId: string;
  at: string;
  by: string;
  checks: Partial<Record<CheckKey, CheckState>>;
  /** what was found, per item */
  notes?: Partial<Record<CheckKey, string>>;
  /* runway thirds only */
  contaminant?: Contaminant;
  depthMm?: number;
  coveragePct?: number;
  oatAtOrBelowMinus15?: boolean;
  /** a lower code the assessor assigned on observation; never higher than the matrix */
  downgradeTo?: number;
  downgradeWhy?: string;
  remark?: string;
}

export interface CheckProblem {
  field: CheckKey | "contaminant" | "depthMm" | "coveragePct" | "downgradeTo";
  message: string;
}

/** What is missing or inconsistent in a check before it is saved. */
export function checkProblems(area: Area, c: Omit<AreaCheck, "id" | "at" | "by" | "areaId">): CheckProblem[] {
  const out: CheckProblem[] = [];
  for (const k of CHECKS_FOR[area.kind])
    if (!c.checks[k]) out.push({ field: k, message: "Mark every item clear, hazard or cleared." });
  if (area.kind === "runway") {
    if (!c.contaminant) out.push({ field: "contaminant", message: "Choose the surface condition." });
    else {
      const def = CONTAMINANT[c.contaminant];
      if (c.contaminant !== "dry" && (c.coveragePct == null || c.coveragePct < 0 || c.coveragePct > 100))
        out.push({ field: "coveragePct", message: "Give the coverage of this third, 0 to 100%." });
      if (def.depth && (c.depthMm == null || c.depthMm < 0))
        out.push({ field: "depthMm", message: "Give the depth in millimetres; it decides the code." });
      if (c.contaminant === "wet" && (c.depthMm ?? 0) > 3)
        out.push({ field: "depthMm", message: "More than 3 mm of water is standing water, not wet." });
      if (c.downgradeTo != null) {
        const m = rwyccFor(c.contaminant, c.depthMm, c.coveragePct ?? 100, c.oatAtOrBelowMinus15);
        if (c.downgradeTo > m)
          out.push({ field: "downgradeTo", message: "A code may be lowered on observation, never raised." });
        if (!c.downgradeWhy?.trim())
          out.push({ field: "downgradeTo", message: "Say what was observed that lowers the code." });
      }
    }
  }
  return out;
}

/* ── an inspection ────────────────────────────────────────────────────── */

export interface LogEntry {
  at: string;
  by: string;
  /** English; also an Arabic key where it has no {placeholder} */
  what: string;
  vars?: Record<string, string | number>;
}

export interface UrgentInspection {
  id: string;
  trigger: TriggerKey;
  triggerNote?: string;
  openedAt: string;
  openedBy: string;
  /** every check made, oldest first; the latest per area is current */
  checks: AreaCheck[];
  log: LogEntry[];
  /** every form sent from this inspection, exactly as sent; append-only — see urgentForms.ts */
  submissions?: Submission[];
  closed?: { at: string; by: string; outcome: "safe" | "restricted"; note?: string; restricted: string[] };
}

export interface UrgentRegister {
  inspections: UrgentInspection[];
}

export interface Who {
  by: string;
  at: string;
  newId: () => string;
}

export function openInspection(
  reg: UrgentRegister,
  trigger: TriggerKey,
  note: string | undefined,
  who: Who,
): UrgentRegister {
  if (reg.inspections.some((i) => !i.closed)) throw new Error("an urgent inspection is already open; close it first");
  const insp: UrgentInspection = {
    id: who.newId(),
    trigger,
    triggerNote: note?.trim() || undefined,
    openedAt: who.at,
    openedBy: who.by,
    checks: [],
    log: [{ at: who.at, by: who.by, what: "Urgent inspection opened after: {trigger}", vars: { trigger } }],
  };
  return { inspections: [...reg.inspections, insp] };
}

export function update(reg: UrgentRegister, id: string, fn: (i: UrgentInspection) => UrgentInspection): UrgentRegister {
  return { inspections: reg.inspections.map((i) => (i.id === id ? fn(i) : i)) };
}

/** Append a check; the earlier checks of the area stay on the record. */
export function recordCheck(
  reg: UrgentRegister,
  inspectionId: string,
  area: Area,
  c: Omit<AreaCheck, "id" | "at" | "by" | "areaId">,
  who: Who,
): UrgentRegister {
  const problems = checkProblems(area, c);
  if (problems.length) throw new Error(problems.map((p) => p.message).join(" "));
  return update(reg, inspectionId, (i) => {
    if (i.closed) throw new Error("this inspection is closed");
    const check: AreaCheck = { ...c, id: who.newId(), areaId: area.id, at: who.at, by: who.by };
    const s = stateOf(area, check);
    const again = i.checks.some((x) => x.areaId === area.id);
    return {
      ...i,
      checks: [...i.checks, check],
      log: [
        ...i.log,
        {
          at: who.at,
          by: who.by,
          what: again ? "{area} inspected again: {verdict}" : "{area} inspected: {verdict}",
          vars: { area: area.id, verdict: s.verdict },
        },
      ],
    };
  });
}

export function noteWorkOrder(
  reg: UrgentRegister,
  inspectionId: string,
  areaId: string,
  number: string,
  who: Who,
): UrgentRegister {
  return update(reg, inspectionId, (i) => ({
    ...i,
    log: [
      ...i.log,
      { at: who.at, by: who.by, what: "Work order {number} raised for {area}", vars: { number, area: areaId } },
    ],
  }));
}

/* ── the state of an area, and of the inspection ─────────────────────── */

export type Verdict = "not-inspected" | "clear" | "caution" | "restricted" | "unsafe";

export const VERDICT_LABEL: Record<Verdict, string> = {
  "not-inspected": "Not inspected",
  clear: "No hazard found",
  caution: "Open — conditions reported",
  restricted: "Restricted",
  unsafe: "Not safe — hazard open",
};

export interface AreaState {
  area: Area;
  latest?: AreaCheck;
  /** how many times it has been checked in this inspection */
  times: number;
  verdict: Verdict;
  /** the matrix code, before any downgrade */
  matrix?: number;
  /** the code reported: the matrix, or the assessor's lower one */
  rwycc?: number;
  hazards: CheckKey[];
  cleared: CheckKey[];
}

export function stateOf(area: Area, latest: AreaCheck | undefined, times = latest ? 1 : 0): AreaState {
  if (!latest) return { area, times, verdict: "not-inspected", hazards: [], cleared: [] };
  const hazards = CHECKS_FOR[area.kind].filter((k) => latest.checks[k] === "found");
  const cleared = CHECKS_FOR[area.kind].filter((k) => latest.checks[k] === "cleared");
  let matrix: number | undefined;
  let rwycc: number | undefined;
  if (area.kind === "runway" && latest.contaminant) {
    matrix = rwyccFor(latest.contaminant, latest.depthMm, latest.coveragePct ?? 100, latest.oatAtOrBelowMinus15);
    rwycc = latest.downgradeTo != null ? Math.min(matrix, latest.downgradeTo) : matrix;
  }
  let verdict: Verdict = "clear";
  if (hazards.length || rwycc === 0) verdict = "unsafe";
  else if (rwycc === 1) verdict = "restricted";
  else if (cleared.length || (rwycc != null && rwycc < 6 && latest.contaminant !== "dry")) verdict = "caution";
  return { area, latest, times, verdict, matrix, rwycc, hazards, cleared };
}

export function statesOf(insp: UrgentInspection, areas: Area[]): AreaState[] {
  return areas.map((a) => {
    const mine = insp.checks.filter((c) => c.areaId === a.id);
    return stateOf(a, mine.at(-1), mine.length);
  });
}

export type Progress = "in-progress" | "ready" | "restricted";

export function progressOf(states: AreaState[]): Progress {
  if (states.some((s) => s.verdict === "not-inspected")) return "in-progress";
  if (states.some((s) => s.verdict === "unsafe" || s.verdict === "restricted")) return "restricted";
  return "ready";
}

/** The areas that stay out of use if the inspection closes now. */
export function restrictedAreas(states: AreaState[]): AreaState[] {
  return states.filter((s) => s.verdict === "unsafe" || s.verdict === "restricted");
}

export function closeInspection(
  reg: UrgentRegister,
  inspectionId: string,
  areas: Area[],
  outcome: "safe" | "restricted",
  note: string | undefined,
  who: Who,
): UrgentRegister {
  return update(reg, inspectionId, (i) => {
    if (i.closed) throw new Error("this inspection is already closed");
    const states = statesOf(i, areas);
    const p = progressOf(states);
    if (p === "in-progress") throw new Error("every area must be inspected before the inspection is closed");
    if (outcome === "safe" && p !== "ready")
      throw new Error("operations cannot be confirmed safe while a hazard is open; close with restrictions");
    const restricted = restrictedAreas(states).map((s) => s.area.id);
    if (outcome === "restricted" && !note?.trim()) throw new Error("say what stays restricted and why");
    return {
      ...i,
      closed: { at: who.at, by: who.by, outcome, note: note?.trim() || undefined, restricted },
      log: [
        ...i.log,
        outcome === "safe"
          ? { at: who.at, by: who.by, what: "Safe operations confirmed" }
          : {
              at: who.at,
              by: who.by,
              what: "Closed with restrictions: {areas}",
              vars: { areas: restricted.join(", ") },
            },
      ],
    };
  });
}

/* ── the runway condition report ──────────────────────────────────────── */

/**
 * The aeroplane-performance line of the RCR for one runway, from its three
 * thirds — "RWY 17 5/5/2 100/50/75 NR/NR/06 WET/WET/STANDING WATER". Null
 * until all three thirds have been inspected: a report with a third guessed
 * is worse than no report.
 */
export function rcrLine(states: AreaState[], runway: RunwayDef): string | null {
  const thirds = ([1, 2, 3] as const).map((n) => states.find((s) => s.area.zone === runway.zone && s.area.third === n));
  if (thirds.some((s) => !s?.latest?.contaminant)) return null;
  const codes = thirds.map((s) => s!.rwycc);
  const cov = thirds.map((s) => {
    const c = s!.latest!;
    if (c.contaminant === "dry") return "NR";
    return String(coverageBand(c.coveragePct ?? 100) ?? "NR");
  });
  const depth = thirds.map((s) => {
    const c = s!.latest!;
    if (!CONTAMINANT[c.contaminant!].depth || (c.coveragePct ?? 100) < 10) return "NR";
    return String(Math.round(c.depthMm ?? 0)).padStart(2, "0");
  });
  const cond = thirds.map((s) => {
    const c = s!.latest!;
    return (c.coveragePct ?? 100) < 10 ? "DRY" : CONTAMINANT[c.contaminant!].rcr;
  });
  return `RWY ${runway.from} ${codes.join("/")} ${cov.join("/")} ${depth.join("/")} ${cond.join("/")}`;
}

/* ── the desk ─────────────────────────────────────────────────────────── */

export interface Figure {
  key: string;
  label: string;
  value: number | null;
  unit: string;
  text: string;
  assumed?: boolean;
  tone?: "good" | "bad" | "warn";
  sources: SourceRef[];
}

export interface UrgentDesk {
  areas: Area[];
  open: UrgentInspection | null;
  /** other inspections open at the same time, from devices that opened one offline */
  alsoOpen: UrgentInspection[];
  states: AreaState[];
  progress: Progress | null;
  rcr: { runway: RunwayDef; line: string | null }[];
  history: UrgentInspection[];
  tiles: Figure[];
  boardLine: string[];
  figures: Set<string>;
}

export function buildUrgentDesk(reg: UrgentRegister, m: MovementArea, now: Date): UrgentDesk {
  const areas = areasOf(m);
  const opened = reg.inspections.filter((i) => !i.closed).sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  /* two devices can each open one while out of signal; the earlier is the one
     worked here, and the other is named rather than hidden */
  const open = opened[0] ?? null;
  const alsoOpen = opened.slice(1);
  const states = open ? statesOf(open, areas) : [];
  const progress = open ? progressOf(states) : null;
  const rcr = m.runways.map((runway) => ({ runway, line: open ? rcrLine(states, runway) : null }));
  const history = reg.inspections.filter((i) => i.closed).sort((a, b) => b.openedAt.localeCompare(a.openedAt));
  const done = states.filter((s) => s.verdict !== "not-inspected").length;
  const hazards = states.filter((s) => s.verdict === "unsafe" || s.verdict === "restricted").length;
  const codes = states.map((s) => s.rwycc).filter((c): c is number => c != null);
  const lowest = codes.length ? Math.min(...codes) : null;
  const minutes = open ? Math.max(0, Math.round((now.getTime() - Date.parse(open.openedAt)) / 60000)) : null;

  const REF: SourceRef = { kind: "entered", ref: t("urgent inspection"), label: t("the urgent inspection record") };
  const tiles: Figure[] = open
    ? [
        {
          key: "done",
          label: t("areas inspected"),
          value: done,
          unit: `/ ${areas.length}`,
          text:
            done === areas.length
              ? t("every area has been inspected")
              : t("{n} still to inspect", { n: areas.length - done }),
          tone: done === areas.length ? "good" : "warn",
          sources: [REF],
        },
        {
          key: "hazards",
          label: t("areas not safe"),
          value: hazards,
          unit: "",
          text: hazards ? t("a hazard is open or a runway third is at RWYCC 1 or 0") : t("no hazard open"),
          tone: hazards ? "bad" : "good",
          sources: [REF],
        },
        {
          key: "rwycc",
          label: t("lowest RWYCC"),
          value: lowest,
          unit: "",
          text: lowest == null ? t("no runway third assessed yet") : t(RWYCC_LABEL[lowest]),
          tone: lowest == null ? "warn" : lowest <= 1 ? "bad" : lowest <= 3 ? "warn" : "good",
          sources: [REF],
        },
        {
          key: "elapsed",
          label: t("minutes since opened"),
          value: minutes,
          unit: t("min"),
          text: t("from the start of the inspection"),
          sources: [REF],
        },
      ]
    : [
        {
          key: "open",
          label: t("urgent inspection open"),
          value: 0,
          unit: "",
          text: t("none open — start one after any weather that may have changed the movement area"),
          tone: "good",
          sources: [REF],
        },
        {
          key: "past",
          label: t("urgent inspections on record"),
          value: history.length,
          unit: "",
          text: history[0] ? t("last {date}", { date: history[0].openedAt.slice(0, 10) }) : t("none recorded"),
          sources: [REF],
        },
      ];

  const trig = (k: TriggerKey) => t(TRIGGERS.find((x) => x.key === k)?.label ?? k).toLowerCase();
  const place = (x: AreaState) =>
    x.area.third ? `${x.area.name} ${t(THIRD_LABEL[x.area.third]).toLowerCase()}` : t(x.area.name);
  const boardLine: string[] = open
    ? [
        t("An urgent inspection is open after {trigger}: {done} of {total} areas inspected.", {
          trigger: trig(open.trigger),
          done,
          total: areas.length,
        }),
        progress === "ready"
          ? t("Every area is inspected and no hazard is open: operations can be confirmed safe.")
          : hazards
            ? t("{count}: {areas}.", {
                count: tn(hazards, "{n} area is not safe", "{n} areas are not safe"),
                areas: restrictedAreas(states).map(place).join(", "),
              })
            : t("No hazard is open so far; operations are not confirmed until every area is inspected."),
        ...rcr.filter((r) => r.line).map((r) => t("Runway condition: {line}.", { line: r.line! })),
      ]
    : [
        t("No urgent inspection is open."),
        history[0]
          ? history[0].closed!.outcome === "safe"
            ? t("The last was opened {date} after {trigger} and closed with operations confirmed safe.", {
                date: history[0].openedAt.slice(0, 10),
                trigger: trig(history[0].trigger),
              })
            : t("The last was opened {date} after {trigger} and closed with restrictions.", {
                date: history[0].openedAt.slice(0, 10),
                trigger: trig(history[0].trigger),
              })
          : t("None is on record."),
      ];
  const figures = new Set<string>(
    [done, areas.length, hazards, lowest, minutes, history.length].filter((x): x is number => x != null).map(String),
  );
  return { areas, open, alsoOpen, states, progress, rcr, history, tiles, boardLine, figures };
}

export const URGENT_MODEL_PROMPT = [
  "You are advising an aerodrome duty manager on an urgent inspection of the movement area after bad weather, from the material given and nothing else.",
  "You may explain, prioritise and draft; you must not state any figure or runway condition code that is not in the material.",
  "Never state that a runway, taxiway or apron is safe to use. That is the aerodrome operator's decision under its procedures, confirmed on this screen.",
  "Be brief and direct.",
].join(" ");

export function materialForUrgent(d: UrgentDesk): string {
  const L: string[] = [];
  for (const tile of d.tiles) L.push(`Tile ${tile.label}: ${tile.value ?? "n/a"} — ${tile.text}`);
  for (const s of d.states) {
    const where = s.area.third ? `${s.area.name} ${THIRD_LABEL[s.area.third]}` : s.area.name;
    L.push(
      `${where}: ${VERDICT_LABEL[s.verdict]}` +
        (s.rwycc != null ? `; RWYCC ${s.rwycc}` : "") +
        (s.latest?.contaminant ? `; ${CONTAMINANT[s.latest.contaminant].label}` : "") +
        (s.hazards.length ? `; hazards: ${s.hazards.map((k) => CHECK_LABEL[k]).join(", ")}` : "") +
        (s.cleared.length ? `; cleared: ${s.cleared.map((k) => CHECK_LABEL[k]).join(", ")}` : ""),
    );
  }
  for (const r of d.rcr)
    L.push(`RCR RWY ${r.runway.designator}: ${r.line ?? "incomplete — not every third inspected"}`);
  L.push(`Board: ${d.boardLine.join(" ")}`);
  return L.join("\n");
}

export function answerUrgent(q: string, d: UrgentDesk): string | null {
  const text = q.toLowerCase();
  const name = (s: AreaState) => (s.area.third ? `${s.area.name} — ${THIRD_LABEL[s.area.third]}` : s.area.name);
  if (!d.open) {
    if (/open|now|status|safe|left|remain/.test(text)) return d.boardLine.join(" ");
    return null;
  }
  if (/left|remain|not (yet )?inspected|to do|outstanding/.test(text)) {
    const left = d.states.filter((s) => s.verdict === "not-inspected");
    return left.length ? left.map(name).join("\n") : "Every area has been inspected.";
  }
  if (/hazard|unsafe|not safe|closed|restrict|problem/.test(text)) {
    const bad = restrictedAreas(d.states);
    return bad.length
      ? bad
          .map(
            (s) =>
              `${name(s)}: ${[...s.hazards.map((k) => CHECK_LABEL[k]), ...(s.rwycc != null && s.rwycc <= 1 ? [`RWYCC ${s.rwycc}`] : [])].join(", ")}`,
          )
          .join("\n")
      : "No hazard is open.";
  }
  if (/rwycc|rcr|runway condition|code|braking|standing water|contamina/.test(text)) {
    return d.rcr.map((r) => `RWY ${r.runway.designator}: ${r.line ?? "not every third inspected yet"}`).join("\n");
  }
  if (/safe|confirm|ready|status/.test(text)) return d.boardLine.join(" ");
  return null;
}

/* ── one record, many writers ─────────────────────────────────────────── */

/**
 * The record as every device wrote it, merged.
 *
 * The console and each phone post their own copy of the inspections they
 * touched, as notes under one topic, and none of them ever edits a note. So
 * the record is the UNION of every copy: inspections by id, their checks by
 * id, their submissions by id, their log lines by what they say. Because
 * nothing in an inspection is ever rewritten — a re-inspection is a new check,
 * a sent form is a new copy — a union loses nothing and invents nothing, and
 * merging a copy twice (a phone resending after its answer was lost in a
 * runway strip) changes nothing. A close is taken from whichever copy has it;
 * two closes of one inspection keep the earlier.
 */
export function mergeUrgent(copies: readonly UrgentRegister[]): UrgentRegister {
  const by = new Map<string, UrgentInspection>();
  const logKey = (e: LogEntry) => JSON.stringify([e.at, e.by, e.what, e.vars ?? null]);
  for (const reg of copies)
    for (const i of reg?.inspections ?? []) {
      const cur = by.get(i.id);
      if (!cur) {
        by.set(i.id, {
          ...i,
          checks: [...(i.checks ?? [])],
          log: [...(i.log ?? [])],
          submissions: i.submissions ? [...i.submissions] : undefined,
        });
        continue;
      }
      const checks = new Map(cur.checks.map((c) => [c.id, c]));
      for (const c of i.checks ?? []) if (!checks.has(c.id)) checks.set(c.id, c);
      const logs = new Map(cur.log.map((e) => [logKey(e), e]));
      for (const e of i.log ?? []) if (!logs.has(logKey(e))) logs.set(logKey(e), e);
      const subs = new Map((cur.submissions ?? []).map((x) => [x.id, x]));
      for (const x of i.submissions ?? []) if (!subs.has(x.id)) subs.set(x.id, x);
      const closed =
        cur.closed && i.closed ? (cur.closed.at <= i.closed.at ? cur.closed : i.closed) : (cur.closed ?? i.closed);
      by.set(i.id, {
        ...cur,
        checks: [...checks.values()],
        log: [...logs.values()],
        submissions: subs.size ? [...subs.values()] : undefined,
        closed,
      });
    }
  const byTime = <T extends { at: string }>(xs: T[]) => xs.sort((a, b) => a.at.localeCompare(b.at));
  return {
    inspections: [...by.values()]
      .map((i) => ({
        ...i,
        checks: byTime(i.checks),
        log: byTime(i.log),
        ...(i.submissions ? { submissions: byTime(i.submissions) } : {}),
      }))
      .sort((a, b) => a.openedAt.localeCompare(b.openedAt)),
  };
}
